<template>
  <div>
    <div class="search-button" @click="openSearch()">
      <i class="fa fa-search"></i>
    </div>
    <div class="backdrop" @click="closeSearch()" v-show="open">
      <div class="search-modal" @click.stop>
        <div class="search-header">
          <div class="filler"></div>
          <div class="search-input">
            <input
              type="search"
              v-model="searchTerm"
              placeholder="Enter Search Term (min 3 chars)"
              ref="searchInput"
              @keyup="
                $event.keyCode === 32
                  ? $event.preventDefault()
                  : startSearch(searchCount)
              "
            />
            <div class="fa-stack" style="margin-right: 0">
              <i class="fa fa-circle fa-stack-1x icon-background"></i>
              <i
                class="fa fa-times fa-stack-1x icon"
                @click="(searchTerm = null), resetSearch()"
              ></i>
            </div>
          </div>
          <div class="fa-stack">
            <i class="fa fa-circle fa-stack-1x icon-background"></i>
            <i class="fa fa-times fa-stack-1x icon" @click="closeSearch()"></i>
          </div>
        </div>
        <div class="search-menu">
          <div
            v-for="section in sections"
            :class="{ active: selected == section }"
            @click="selected = section"
            :key="section"
          >
            {{ section | deslugify }}
            ({{ results[section].count }})
          </div>
        </div>
        <div class="search-results">
          <div class="loading" v-if="loading">
            <loader />
          </div>
          <div class="no-results" v-else-if="!results[selected].count">
            <h4>
              {{
                searchTerm
                  ? `No ${noResultsText[selected]} found`
                  : `Enter Search Term`
              }}
            </h4>
          </div>
          <scroll-area
            v-else
            ref="scrollbar"
            :showScrollbar="true"
            :dragScrollEnabled="false"
          >
            <div class="videos" v-if="selected == 'videos'">
              <router-link
                @click.native="closeSearch()"
                v-for="video in results.videos.results"
                :key="video.video_title"
                :to="{
                  name: 'videoPlayer',
                  params: {
                    courseId: video.course_id,
                    instanceId: video.instance_id,
                    videoId: video.video_id,
                  },
                  query: { term: video.term, week: video.week },
                }"
              >
                <div class="image">
                  <img class="background" :src="video.thumbnail" />
                  <img
                    class="play-button"
                    src="../../assets/img/play-button.png"
                  />
                </div>
                <div class="text">
                  <h4 v-html="video.title_highlights"></h4>
                  <p>{{ "Week " + video.week + " - " + video.course_name }}</p>
                </div>
              </router-link>
            </div>
            <div class="resources" v-else-if="selected == 'resources'">
              <a
                v-for="resource in results.resources.results"
                :key="resource.name"
                :href="
                      !resource.videoId
                        ? resource.url
                        : `${baseUrl}videos/${resource.courseId}/${resource.instanceId}/${resource.videoId}?week=${resource.week}&tab=resources`
                    "
                    @click="closeSearch()"
              >
                <h4><span v-html="resource.name_highlights"></span>
                  
                </h4>
                <p v-html="resource.description_highlights"></p>
                <p class="course">
                  {{
                    resource.video.title +
                    ", Week " +
                    resource.week +
                    " - " +
                    resource.course.name
                  }}
                </p>
              </a>
            </div>
            <div class="kb" v-else-if="selected == 'KB'">
              <router-link
                v-for="article in results.KB.results"
                :key="article.title"
                @click.native="closeSearch()"
                :title="article.title"
                :to="{
                  name: 'KB Article',
                  params: { articleId: article.id },
                }"
              >
                <h4 ><span v-html="article.title_highlights"></span></h4>
                <p v-html="article.overview_highlights"></p
              ></router-link>
            </div>
            <div class="forum" v-else-if="selected == 'forum'">
              <div v-for="post in results.forum.results" :key="post.id">
                <h4>
                  <router-link
                    @click.native="closeSearch()"
                    :to="getThreadUrl(post)"
                    v-html="post.title_highlights"
                  >
                  </router-link>
                </h4>
                <p>
                  <span v-html="post.body_highlights"></span
                  ><router-link
                    @click.native="closeSearch()"
                    :to="getThreadUrl(post)"
                  >
                    Read Forum Post
                  </router-link>
                </p>
                <p class="author">
                  -
                  <i>{{ post.author.firstName }} {{ post.author.lastName }}</i>
                  @ {{ post.createdAt | shortDate }} in
                  <router-link
                    @click.native="closeSearch()"
                    :to="{
                      name: 'forum-category',
                      params: { categorySlug: post.thread.category.name },
                    }"
                  >
                    {{ post.thread.category.label }}
                  </router-link>
                </p>
              </div>
            </div>
            <div class="chat" v-else-if="selected == 'chat'">
              <div v-for="message in results.chat.results" :key="message._id">
                <p>
                  <span v-html="highlight(message.msg)"></span
                  ><a
                    :href="
                      'https://chat.tttc.ca/channel/' +
                      nameFromId(message.rid) +
                      '?msg=' +
                      message._id
                    "
                    target="_blank"
                  >
                    Read in Think Tank Chat
                  </a>
                </p>
                <p class="author">
                  - <i>{{ message.u.username }} </i> @
                  {{ message.ts || shortDate }} in
                  <a
                    :href="
                      'https://chat.tttc.ca/channel/' + nameFromId(message.rid)
                    "
                    target="_blank"
                    >{{ nameFromId(message.rid) | capitalize }}</a
                  >
                </p>
              </div>
            </div>
          </scroll-area>
        </div>
        <div class="search-pagination">
          <div
            v-for="i in results[selected].pages"
            :class="{ active: offsets[selected] + 1 == i }"
            @click="
              offsets[selected] = i - 1;
              search(selected);
            "
            :key="i"
          >
            {{ i }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import search from "../../services/search";
import Loader from "./Loader.vue";
import toastr from "toastr";
import ForumUtils from "src/utils/forum";
import ScrollArea from "../reusable/scroll-area";

export default {
  data() {
    return {
      baseUrl: window.baseUrl + "/#/",
      open: false,
      loading: false,
      searchTerm: null,
      st: "",
      noResultsText: {
        videos: "lesson videos",
        KB: "knowledge base articles",
        forum: "forum posts",
        chat: "chat posts",
        resources: "resources",
      },
      sections: ["videos", "resources", "KB", "forum", "chat"],
      selected: "videos",
      searchCount: 0,
      results: {
        videos: {
          pages: 0,
          results: null,
          count: 0,
        },
        resources: {
          pages: 0,
          results: null,
          count: 0,
        },
        forum: {
          pages: 0,
          results: null,
          count: 0,
        },
        chat: {
          pages: 0,
          results: null,
          count: 0,
        },
        KB: {
          pages: 0,
          results: null,
          count: 0,
        },
      },
      offsets: {
        videos: 0,
        resources: 0,
        forum: 0,
        chat: 0,
        KB: 0,
      },
      pageSize: 12,
    };
  },
  components: {
    Loader,
    ScrollArea,
  },
  methods: {
    resetSearch() {
      this.results = {
        videos: {
          pages: 0,
          results: null,
          count: 0,
        },
        resources: {
          pages: 0,
          results: null,
          count: 0,
        },
        forum: {
          pages: 0,
          results: null,
          count: 0,
        },
        chat: {
          pages: 0,
          results: null,
          count: 0,
        },
        KB: {
          pages: 0,
          results: null,
          count: 0,
        },
      };
      this.offsets = {
        videos: 0,
        resources: 0,
        forum: 0,
        chat: 0,
        KB: 0,
      };
    },
    startSearch(count) {
      setTimeout(() => {
        if (this.searchCount == count + 1) this.search();
      }, 1000);
      this.searchCount++;
    },
    search(type) {
      if (this.searchTerm.trim().length < 3) {
        return;
      }
      this.st = this.searchTerm.replace(/[^a-zA-Z0-9-_ ]/g, "");

      this.st.split(" ").forEach((term) => {
        if (term.includes("-")) this.st += " " + term.replace("-", "");
      });

      if (this.st.length < 3) return;

      let offset;

      this.loading = true;

      if (type) {
        offset = this.offsets[type];
      } else {
        this.resetSearch();
      }

      search
        .search(this.st, type, offset)
        .then((response) => {
          console.log(response);
          this.loading = false;
          for (let type of Object.keys(this.results)) {
            if (response.results[type]) {
              this.results[type].results = response.results[type];
              if (
                !response.results[type][0] ||
                !response.results[type][0].full_count
              ) {
                this.results[type].pages = 0;
              } else {
                this.results[type].pages = Math.ceil(
                  response.results[type][0].full_count / this.pageSize
                );
                this.results[type].count = response.results[type][0].full_count;
              }
            }
          }
        })
        .catch((e) => {
          toastr.error("Error getting results");
          this.loading = false;
        });
    },
    openSearch() {
      this.open = true;
      document.body.className = "stop-scrolling";
      setTimeout(() => {
        this.$refs.searchInput.focus();
      }, 200);
    },
    closeSearch() {
      this.open = false;
      document.body.className = "";
    },
    nameFromId(id) {
      const rooms = {
        GENERAL: "00-General",
        uCb6tQvNeK9KodGoh: "05-Sculpting",
        XGiCBZYDtEptnFKFy: "06-Technical",
        "5irJLcGDwwzFaNrNb": "07-Work_in_Progress",
        "6cHGqCrnb9Au97Rwj": "08-Inspiration",
        PDAEbcsz65rBwXq7R: "10-Portal",
        "3yZzeX2DGngysWFY6": "09-Resources",
        ZqPMPcXiSb9crRExp: "04-Lookdev",
        BjTG3YAu9mbpbSJwA: "01-Maya",
        HZchuCPTNnPN5PnHC: "03-Texturing",
        khPKFa6KvxMPDSDXR: "02-Modeling",
        tr5qqPpTEfFsP93W5: "11-Animation",
      };
      return rooms[id];
    },
    highlight(value) {
      let s = value;
      this.st
        .trim()
        .split(" ")
        .forEach((term) => {
          if (term.length > 3) {
            let iQuery = new RegExp(term, "ig");
            s = s.toString().replace(iQuery, function (matchedTxt, a, b) {
              return "<b>" + matchedTxt + "</b>";
            });
          }
        });
      return s;
    },
    getThreadUrl(post) {
      const slug = ForumUtils.formatTitlePreview(post.thread.title);
      return {
        name: "forum-thread",
        params: {
          threadId: post.threadId,
          threadTitlePreview: slug,
        },
      };
    },
  },
};
</script>
<style lang="scss">
.search-button {
  cursor: pointer;
  z-index: 9999;

  i {
    position: relative;
    margin-left: 20px;
    margin-right: 8px;
    margin-bottom: 3px;
  }
}

.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  backdrop-filter: blur(4px);
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.404);
  z-index: 9999999;
  display: flex;
  align-items: center;
  justify-content: center;

  .search-modal {
    width: 100%;
    max-width: 1024px;
    height: 100%;
    max-height: 950px;
    background: #333;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    display: flex;
    flex-direction: column;

    .search-header {
      width: 100%;
      min-height: 60px;
      background: darken(#333, 10%);
      display: flex;
      align-items: center;
      justify-content: center;

      .filler {
        margin-right: auto;
        width: 4rem;
      }
      .search-input {
        position: relative;

        input[type="search"] {
          width: 300px;
          max-width: 100%;
          background: #222;
          padding: 8px 4px;
          border: 1px solid #b0b0b0;
          color: #b0b0b0;

          &:focus {
            outline: none;
          }
        }

        .fa-stack {
          position: absolute;
          right: 0;
          top: 5px;
          cursor: pointer;

          .icon-background {
            color: transparent;
            font-size: 3rem;
          }

          &:hover {
            .icon-background {
              color: #d3d3d34b;
            }

            .icon {
              color: white;
            }
          }
        }
      }

      .fa-stack {
        margin-left: auto;
        margin-right: 1rem;

        .icon-background {
          color: transparent;
          font-size: 3rem;
        }

        &:hover {
          .icon-background {
            color: #d3d3d34b;
          }

          .icon {
            color: white;
          }
        }
      }
    }

    .search-menu {
      border-bottom: 1px solid #555;
      width: 100%;
      min-height: 40px;
      font-style: italic;
      display: flex;
      justify-content: space-around;
      align-items: center;

      div {
        cursor: pointer;

        &.active {
          color: #3cf;
        }
      }
    }

    .search-results {
      position: relative;
      flex-grow: 1;
      overflow-y: hidden;

      .no-results {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .videos {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        padding: 2rem;

        a {
          width: calc(50% - 1rem);
          display: flex;
          align-items: center;
          margin: 1rem 0;
          padding: 1rem;
          background: darken(#333, 5%);

          &:hover {
            text-decoration: none;
            background: darken(#333, 8%);
          }

          .image {
            width: 33%;
            aspect-ratio: 16/9;
            position: relative;

            .background {
              padding: 2px;
              background-color: #222222;
              border: 1px solid #464545;
              width: 100%;
              height: 100%;
            }
            .play-button {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              margin: auto;
              float: none;
            }
          }

          .text {
            width: 60%;
            display: flex;
            flex-direction: column;
            margin-left: 2rem;
          }
        }
      }

      .forum,
      .chat {
        padding: 2rem;

        div {
          background: darken(#333, 5%);
          padding: 1rem;
          display: block;
          margin: 1rem 0;

          &:hover {
            background: darken(#333, 8%);
          }
          p {
            cursor: default;

            b {
              color: white;
            }

            a {
              text-decoration: none;
            }
          }

          h4 {
            a {
              text-decoration: none;
              color: #fff;
              font-weight: 100;
              &:hover {
                color: #3cf;
              }
            }
          }

          .author {
            margin-left: 1rem;

            a {
              text-decoration: none;
            }
          }

          .course {
            color: #3cf;
          }
        }
      }

      .resources,
      .kb {
        padding: 2rem;

        a {
          background: darken(#333, 5%);
          padding: 1rem;
          display: block;
          margin: 1rem 0;
          color: #b0b0b0;
          text-decoration: none;

          &:hover {
            background: darken(#333, 8%);
          }
          p {
            b {
              color: white;
            }
          }

          h4 {
            color: #fff;
            font-weight: 100;
            span {
              &:hover {
                color: #3cf;
              }
            }
          }

          .author {
            margin-left: 1rem;

            a {
              text-decoration: none;
            }
          }

          .course {
            color: #3cf;
          }
        }
      }
    }

    .search-pagination {
      border-top: 1px solid #555;
      width: 100%;
      min-height: 40px;
      font-style: italic;
      display: flex;
      justify-content: center;
      align-items: center;

      div {
        background: lighten(#333, 10%);
        width: 2.4rem;
        height: 2.4rem;
        text-align: center;
        line-height: 2.4rem;
        margin: 0 7px;
        cursor: pointer;
        &.active {
          background: #3cf;
          color: #000;
        }
      }
    }

    .loading {
      position: absolute;
      background: #333;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
    }
  }
}
</style>

<style>
.stop-scrolling {
  height: 100%;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// Search.vue?60da01d5